import * as API from '@api';
import {
  type DashboardSearchParams,
  DocumentStatus,
  type DocumentsList,
  DocumentVerificationStatus,
  type Professional,
  type SendingRequestBundle,
  SendingsDashboardType,
} from '@domain/types';
import { DocumentProcessingController } from '@launcher/context/DocumentProcessing.context';
import { LoadingDocumentsController } from '@launcher/context/LoadingDocuments.context';
import { invalidateReadyToSendCount } from '@lifen-sending/features/draft/utils/invalidateReadyToSendCount.util';
import { createSendingRequestCache } from '@lifen-sending/utils/sendingRequestCache';
import { findSendingRequestV2InCache } from '@lifen-sending/utils/sendingRequestsV2Cache.util';
import { analysisFallbackPoller } from '@services/analysisFallbackPoller.service';
import {
  notifyImportAnalysisComplete,
  registerChildImports,
} from '@services/analytics/fileImportCompletedTracker';
import { legacyFeatureFlagsStore } from '@services/featureFlags/legacyFeatureFlags.store';
import { logger } from '@services/logger/logger.service';
import { updateEntitiesAction } from '@store/documents/documents.actions';
import { selectDashboardSearchParams } from '@store/documents/documents.selector';
import { selectDraftCurrentIdentityReference } from '@store/documents/outgoing/draft.selector';
import { refreshDraftCounters } from '@store/documents/outgoing/draft.slice';
import {
  type RealtimeCommunicationRequestUpdated,
  realtimeCommunicationRequestUpdated,
  realtimePublipostageCrCreated,
} from '@store/externalEvents/externalEvents.action';
import { selectIdentitiesWithoutDevice } from '@store/user/user.selector';
import type { QueryClient } from '@tanstack/react-query';
import {
  addDraftDocumentInCache,
  getDashboardDocumentsQueryKey,
  invalidateDashboardCache,
  invalidateDraftDocumentsInCache,
  refreshDraftCacheAfterActionOnOneDocument,
} from '@utils/caching.util';
import { selectPathName } from '@utils/document.util';
import { professionalToIdentityReference } from '@utils/identities.util';
import { getDashboardTypeFromRoute, isDashboardRoute, isDetailRoute } from '@utils/route.utils';
import { all, getContext, put, select, takeEvery } from 'redux-saga/effects';
import { resolveTerminalImportStatus } from '../../common/document-import/resolveTerminalImportStatus';

function updateDocumentProcessingContextFromBundle(
  documentId: string,
  bundle: SendingRequestBundle,
) {
  const terminalStatus = resolveTerminalImportStatus(bundle);
  if (!terminalStatus) {
    return false;
  }

  if (terminalStatus === 'ANALYZE_UNAVAILABLE') {
    logger.info(
      'LOGIC',
      `[DocumentProcessing] Document ${documentId} analysis unavailable (manual mode)`,
    );
  } else {
    logger.info(
      'LOGIC',
      `[DocumentProcessing] Document ${documentId} completed with status: ${bundle.document.verificationStatus}`,
    );
  }

  DocumentProcessingController.updateStatus(documentId, terminalStatus);
  notifyImportAnalysisComplete(documentId, terminalStatus, 'realtime');
  return true;
}

function receiveSendingRequestUpdated() {
  return function* saga({ payload }: ReturnType<RealtimeCommunicationRequestUpdated>) {
    const queryClient: QueryClient = yield getContext('queryClient');
    try {
      const documentId = payload.resource.id;

      logger.info('LOGIC', `[realtime] refreshing document id: ${documentId}`);

      if (!documentId) return;

      const pathname: string = yield select(selectPathName);
      const currentDashboardType = getDashboardTypeFromRoute(pathname) as SendingsDashboardType;
      const currentIdentity: string | undefined = yield select((state) =>
        selectDraftCurrentIdentityReference(state),
      );
      const featureFlags = legacyFeatureFlagsStore.get();
      const useNewUploadUI = featureFlags['enable-new-import-analyze-flow'];
      const useV2SendingDashboards = Boolean(featureFlags['cafe-doc-use-v2-sending-endpoint']);
      const sendingRequestCache = createSendingRequestCache(queryClient, {
        useV2SendingDashboards,
      });

      // Before any early return: what a server echo invalidates depends on the event, not on where
      // the user happens to be standing.
      sendingRequestCache.remoteChange(documentId);

      // For new upload UI: fetch bundle once, used for both context and cache updates.
      // newUploadBundle is set on success; newUploadUIFetchFailed distinguishes a fetch error
      // from a burst event (where no bundle is expected and no fallback is needed).
      let newUploadBundle: SendingRequestBundle | undefined;
      let newUploadUIFetchFailed = false;
      if (useNewUploadUI) {
        const isDocumentBurst = payload.resource.from_document_burst === true;
        const splitIds = payload.resource.split_ids;

        if (isDocumentBurst && splitIds && splitIds.length > 0) {
          analysisFallbackPoller.stopPolling(documentId);
          logger.info('LOGIC', `[DocumentProcessing] Document burst detected for ${documentId}`, {
            splitIds,
            count: splitIds.length,
          });
          registerChildImports(documentId, splitIds, 'split');
          DocumentProcessingController.registerDocumentBurst(documentId, splitIds);
          splitIds.forEach((childId) => analysisFallbackPoller.startPolling(childId));
        } else {
          try {
            const bundle: SendingRequestBundle =
              payload.prefetchedBundle ??
              (yield API.fetchSendingRequest(documentId, SendingsDashboardType.Draft, true));
            newUploadBundle = bundle;
            const isTerminal = updateDocumentProcessingContextFromBundle(documentId, bundle);
            if (isTerminal) {
              analysisFallbackPoller.stopPolling(documentId);
            }
          } catch (error) {
            logger.error(
              'LOGIC',
              `Failed to fetch document ${documentId} for context update`,
              error,
            );
            newUploadUIFetchFailed = true;
          }
        }
      }

      if (
        !currentDashboardType ||
        (currentDashboardType !== SendingsDashboardType.Draft &&
          currentDashboardType !== SendingsDashboardType.Sent)
      ) {
        // If we receive a pusher notif while not in Document Application
        // We only invalidate the Details page as the cache might have
        //  been preloaded from a Lifen Integration forward
        invalidateDraftDocumentsInCache({ documentId, queryClient });
        return;
      }

      const searchParams: DashboardSearchParams = yield select((state) =>
        selectDashboardSearchParams(state, currentDashboardType),
      );

      const isCurrentDetailPage = isDetailRoute(pathname) && pathname.match(documentId);
      // Under the list FF the v1 dashboard cache holds at most the one page FeatureFlags.provider
      // prefetches at startup, so presence there reads false for anything else — look the document
      // up in the v2 cache instead in that case.
      const isDocumentInDashboard = useV2SendingDashboards
        ? !!findSendingRequestV2InCache(queryClient, documentId)
        : queryClient
            .getQueriesData<DocumentsList>({
              queryKey: getDashboardDocumentsQueryKey({
                dashboardType: currentDashboardType,
                identity: currentIdentity,
                search: { searchParams, featureFlags },
              }),
            })
            .some((query) => query[1]?.entities?.documents?.some((doc) => doc.id === documentId));
      const isCurrentDashboard = isDashboardRoute(pathname) && isDocumentInDashboard;

      // v1 only: under the list FF nothing renders that cache, so its one live effect is the v2
      // sweep it hides — already covered above, with a wider scope.
      if (!useV2SendingDashboards && isDocumentInDashboard) {
        invalidateDashboardCache({
          dashboardType: currentDashboardType,
          search: { searchParams, featureFlags },
          refetchType: 'active', // Refresh in background,
          queryClient,
        });
      }

      // document is not present on current dashboard page or current detail view
      if (!isCurrentDetailPage && !isCurrentDashboard) {
        if (currentDashboardType === SendingsDashboardType.Draft) {
          if (!useV2SendingDashboards) {
            // Document is not in current cache. Invalidate all other ones except current one.
            invalidateDashboardCache({
              dashboardType: SendingsDashboardType.Draft,
              type: 'inactive', // Invalidate only inactive cache (ie. not the current one)
              refetchType: 'none', // Do not refetch immediatly
              queryClient,
            });

            if (currentIdentity) {
              invalidateDashboardCache({
                dashboardType: SendingsDashboardType.Draft,
                identity: currentIdentity,
                type: 'inactive',
                refetchType: 'none',
                queryClient,
              });
            }
          }

          // Feeds the sidebar counters through Redux, which the v2 dashboards still read.
          yield put(refreshDraftCounters());

          if (newUploadBundle) {
            if (
              newUploadBundle.document.status === DocumentStatus.Suspended &&
              newUploadBundle.document.verificationStatus === DocumentVerificationStatus.ReadyToSend
            ) {
              invalidateReadyToSendCount(queryClient);
            }

            const shouldAddToCache =
              newUploadBundle.document.verificationStatus ===
                DocumentVerificationStatus.ReadyToSend ||
              newUploadBundle.document.verificationStatus ===
                DocumentVerificationStatus.UserVerificationNeeded ||
              newUploadBundle.document.verificationStatus === DocumentVerificationStatus.Downgraded;

            // v1 only: seeding the row by hand saves the v1 dashboard a refetch. v2 gets the
            // arrival from the list invalidation at the top of the handler.
            if (shouldAddToCache && !useV2SendingDashboards) {
              const documentIdentity = professionalToIdentityReference(
                newUploadBundle.document.sender,
              );
              const identities: Professional[] = yield select(selectIdentitiesWithoutDevice);
              const hasManyIdentities = identities.length > 1;

              addDraftDocumentInCache({
                newDocument: newUploadBundle.document,
                newDocumentEntities: newUploadBundle.entities,
                identity: documentIdentity,
                refetchType: 'active',
                searchParams,
                queryClient,
                featureFlags,
              });

              if (hasManyIdentities) {
                addDraftDocumentInCache({
                  newDocument: newUploadBundle.document,
                  newDocumentEntities: newUploadBundle.entities,
                  refetchType: 'active',
                  searchParams,
                  queryClient,
                  featureFlags,
                });
              }
            }
          } else if (newUploadUIFetchFailed && !useV2SendingDashboards) {
            // Fetch failed (403, etc.) → fallback to full invalidation. v1 only: v2 was swept at
            // the top of the handler, bundle or no bundle.
            invalidateDashboardCache({
              dashboardType: SendingsDashboardType.Draft,
              identity: currentIdentity,
              refetchType: 'active',
              queryClient,
            });
          }
        }

        return;
      }

      // Document is present on current dashboard page or current detail view
      // Reuse already-fetched bundle if available (new upload UI), otherwise fetch now
      const bundle: SendingRequestBundle =
        newUploadBundle ??
        (yield API.fetchSendingRequest(documentId, SendingsDashboardType.Draft, true));

      if (
        bundle.document.status === DocumentStatus.Suspended &&
        bundle.document.verificationStatus === DocumentVerificationStatus.ReadyToSend
      ) {
        invalidateReadyToSendCount(queryClient);
      }

      if (bundle.document.status === DocumentStatus.Cancelled) {
        invalidateReadyToSendCount(queryClient);

        // v1 only: this attributes the identity by reading the v1 cache, so under the FF it would
        // fall back to the current identity and sweep the wrong bucket.
        if (!useV2SendingDashboards) {
          refreshDraftCacheAfterActionOnOneDocument({
            documentId: bundle.document.id,
            currentIdentity,
            search: { searchParams, featureFlags },
            queryClient,
          });
        }

        yield put(refreshDraftCounters());
        LoadingDocumentsController.clearLoadingDocuments();
      } else {
        yield put(updateEntitiesAction(bundle.entities));
        sendingRequestCache.remoteUpdate(bundle);
      }
    } catch (err) {
      logger.error('LOGIC', '[realtime] Failed to refresh draft document', err);
    }
  };
}

function receivePublipostageCrCreated() {
  // biome-ignore lint/correctness/useYield: still a saga, the flag read used to be a yield
  return function* saga({ payload }: ReturnType<typeof realtimePublipostageCrCreated>) {
    try {
      const documentId = payload.resource.id;
      const parentDocumentId = payload.resource.parent_document_id;

      if (!documentId || !parentDocumentId) return;

      const featureFlags = legacyFeatureFlagsStore.get();
      const useNewUploadUI = featureFlags['enable-new-import-analyze-flow'];
      if (!useNewUploadUI) return;

      DocumentProcessingController.addPublipostageDocument(parentDocumentId, documentId);
      registerChildImports(parentDocumentId, [documentId], 'publipostage');
      analysisFallbackPoller.startPolling(documentId);
    } catch (err) {
      logger.error('LOGIC', '[realtime] Failed to handle publipostage document creation', err);
    }
  };
}

export function* sendingRequestRealTimeSagas() {
  yield all([
    takeEvery(realtimeCommunicationRequestUpdated.type, receiveSendingRequestUpdated()),
    takeEvery(realtimePublipostageCrCreated.type, receivePublipostageCrCreated()),
  ]);
}
